import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Brain, Sparkles, TrendingUp, User, Clock, Star, ArrowRight, Target, Lightbulb, Zap } from 'lucide-react';
import { Agent } from '../lib/supabase';
import { AgentCard } from './AgentCard';

interface SmartRecommendationsProps {
  onAgentSelect?: (agent: Agent) => void;
}

interface UserNeed {
  id: string;
  text: string;
  category: string;
  complexity: 'beginner' | 'intermediate' | 'advanced';
  keywords: string[];
  icon: React.ComponentType<any>;
  color: string;
}

interface RecommendationResult {
  agent: Agent;
  score: number;
  reasons: string[];
  matchType: 'perfect' | 'good' | 'potential';
}

const COMMON_NEEDS: UserNeed[] = [
  {
    id: 'customer-support',
    text: 'I need to automate customer support',
    category: 'Chatbots',
    complexity: 'beginner',
    keywords: ['customer', 'support', 'chat', 'help', 'service'],
    icon: User,
    color: 'bg-blue-500'
  },
  {
    id: 'content-creation',
    text: 'I want to generate content and copy',
    category: 'Content Creation',
    complexity: 'beginner',
    keywords: ['content', 'writing', 'copy', 'blog', 'marketing'],
    icon: Lightbulb,
    color: 'bg-purple-500'
  },
  {
    id: 'data-analysis',
    text: 'I need to analyze data and generate insights',
    category: 'Data Analysis',
    complexity: 'intermediate',
    keywords: ['data', 'analysis', 'insights', 'reports', 'analytics'],
    icon: TrendingUp,
    color: 'bg-green-500'
  },
  {
    id: 'code-assistant',
    text: 'I want help with coding and development',
    category: 'Code Generation',
    complexity: 'intermediate',
    keywords: ['code', 'programming', 'development', 'debug', 'software'],
    icon: Brain,
    color: 'bg-orange-500'
  },
  {
    id: 'workflow-automation',
    text: 'I need to automate business workflows',
    category: 'Automation',
    complexity: 'advanced',
    keywords: ['automation', 'workflow', 'business', 'process', 'integration'],
    icon: Zap,
    color: 'bg-red-500'
  },
  {
    id: 'research-assistant',
    text: 'I want an AI research assistant',
    category: 'Research',
    complexity: 'beginner',
    keywords: ['research', 'information', 'search', 'knowledge', 'assistant'],
    icon: Target,
    color: 'bg-indigo-500'
  }
];

export interface SmartRecommendationsRef {
  generateRecommendations: (need: UserNeed) => Promise<void>;
}

export const SmartRecommendations = forwardRef<SmartRecommendationsRef, SmartRecommendationsProps>(({ onAgentSelect }, ref) => {
  const [selectedNeed, setSelectedNeed] = useState<UserNeed | null>(null);
  const [customNeed, setCustomNeed] = useState('');
  const [recommendations, setRecommendations] = useState<RecommendationResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [showCustomInput, setShowCustomInput] = useState(false);

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      // In a real implementation, this would fetch user interaction history
      // For now, we'll simulate user preferences
      setUserProfile({
        preferredComplexity: 'beginner',
        favoriteCategories: ['Chatbots', 'Content Creation'],
        recentInteractions: ['customer-support', 'content-creation'],
        skillLevel: 'intermediate'
      });
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const analyzeNeed = async (needText: string): Promise<UserNeed> => {
    // AI-powered need analysis
    const lowerText = needText.toLowerCase();
    
    // Determine category based on keywords
    let category = 'Other';
    let complexity: 'beginner' | 'intermediate' | 'advanced' = 'beginner';
    let keywords: string[] = [];

    if (lowerText.includes('chat') || lowerText.includes('customer') || lowerText.includes('support')) {
      category = 'Chatbots';
      keywords = ['chat', 'customer', 'support', 'conversation'];
    } else if (lowerText.includes('content') || lowerText.includes('writing') || lowerText.includes('blog')) {
      category = 'Content Creation';
      keywords = ['content', 'writing', 'blog', 'copy'];
    } else if (lowerText.includes('data') || lowerText.includes('analysis') || lowerText.includes('analytics')) {
      category = 'Data Analysis';
      complexity = 'intermediate';
      keywords = ['data', 'analysis', 'analytics', 'insights'];
    } else if (lowerText.includes('code') || lowerText.includes('programming') || lowerText.includes('development')) {
      category = 'Code Generation';
      complexity = 'intermediate';
      keywords = ['code', 'programming', 'development'];
    } else if (lowerText.includes('automat') || lowerText.includes('workflow') || lowerText.includes('process')) {
      category = 'Automation';
      complexity = 'advanced';
      keywords = ['automation', 'workflow', 'process'];
    } else if (lowerText.includes('research') || lowerText.includes('information') || lowerText.includes('search')) {
      category = 'Research';
      keywords = ['research', 'information', 'search'];
    }

    // Determine complexity based on technical terms
    if (lowerText.includes('api') || lowerText.includes('integration') || lowerText.includes('enterprise')) {
      complexity = 'advanced';
    } else if (lowerText.includes('custom') || lowerText.includes('specific') || lowerText.includes('complex')) {
      complexity = 'intermediate';
    }

    return {
      id: 'custom',
      text: needText,
      category,
      complexity,
      keywords,
      icon: Brain,
      color: 'bg-gray-500'
    };
  };

  const generateRecommendations = async (need: UserNeed) => {
    setLoading(true);
    
    try {
      // Simulate API call to get recommendations
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/smart-recommendations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            need: need,
            userProfile: userProfile,
            limit: 6
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get recommendations');
      }

      const data = await response.json();
      
      if (data.success) {
        setRecommendations(data.recommendations);
      } else {
        throw new Error(data.error || 'Failed to generate recommendations');
      }
    } catch (error) {
      console.error('Error generating recommendations:', error);
      // Fallback to mock recommendations
      setRecommendations(generateMockRecommendations(need));
    } finally {
      setLoading(false);
    }
  };

  const generateMockRecommendations = (need: UserNeed): RecommendationResult[] => {
    // Mock recommendations based on the need
    const mockAgents: Agent[] = [
      {
        id: '1',
        name: 'CustomerBot Pro',
        description: 'Advanced customer support chatbot with natural language understanding',
        source: 'github',
        source_url: 'https://github.com/example/customerbot',
        repository_url: 'https://github.com/example/customerbot',
        author: 'AI Solutions',
        category: 'Chatbots',
        tags: ['customer-support', 'nlp', 'chatbot'],
        language: 'Python',
        framework: 'LangChain',
        stars: 1250,
        downloads: 5000,
        last_updated: '2024-01-15',
        is_verified: true,
        metadata: {},
        created_at: '2024-01-01',
        updated_at: '2024-01-15'
      },
      {
        id: '2',
        name: 'ContentGenie',
        description: 'AI-powered content generation for blogs, social media, and marketing',
        source: 'huggingface',
        source_url: 'https://huggingface.co/example/contentgenie',
        repository_url: 'https://huggingface.co/example/contentgenie',
        author: 'ContentAI',
        category: 'Content Creation',
        tags: ['content', 'writing', 'marketing'],
        language: 'Python',
        framework: 'Transformers',
        stars: 890,
        downloads: 3200,
        last_updated: '2024-01-10',
        is_verified: true,
        metadata: {},
        created_at: '2024-01-01',
        updated_at: '2024-01-10'
      },
      {
        id: '3',
        name: 'DataInsight AI',
        description: 'Automated data analysis and insight generation from CSV and databases',
        source: 'github',
        source_url: 'https://github.com/example/datainsight',
        repository_url: 'https://github.com/example/datainsight',
        author: 'DataCorp',
        category: 'Data Analysis',
        tags: ['data', 'analysis', 'insights'],
        language: 'Python',
        framework: 'Pandas',
        stars: 2100,
        downloads: 8500,
        last_updated: '2024-01-12',
        is_verified: true,
        metadata: {},
        created_at: '2024-01-01',
        updated_at: '2024-01-12'
      }
    ];

    return mockAgents.map((agent, index) => ({
      agent,
      score: 95 - (index * 10),
      reasons: [
        `Perfect match for ${need.category.toLowerCase()}`,
        `High user rating (${agent.stars} stars)`,
        `Recently updated (${agent.last_updated})`,
        `Matches your skill level`
      ],
      matchType: index === 0 ? 'perfect' : index === 1 ? 'good' : 'potential'
    }));
  };

  const handleNeedSelect = async (need: UserNeed) => {
    setSelectedNeed(need);
    await generateRecommendations(need);
  };

  const handleCustomNeedSubmit = async () => {
    if (!customNeed.trim()) return;
    
    const analyzedNeed = await analyzeNeed(customNeed);
    setSelectedNeed(analyzedNeed);
    await generateRecommendations(analyzedNeed);
    setShowCustomInput(false);
  };

  useImperativeHandle(ref, () => ({
    generateRecommendations: generateRecommendations,
  }));

  const getMatchTypeColor = (matchType: string) => {
    switch (matchType) {
      case 'perfect': return 'text-green-600 bg-green-50 border-green-200';
      case 'good': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'potential': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getMatchTypeIcon = (matchType: string) => {
    switch (matchType) {
      case 'perfect': return '🎯';
      case 'good': return '👍';
      case 'potential': return '💡';
      default: return '🤔';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Smart Agent Recommendations
            </h2>
            <p className="text-gray-600">Tell us what you need, and we'll find the perfect AI agents for you</p>
          </div>
        </div>
      </div>

      {/* User Profile Insights */}
      {userProfile && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-6">
          <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <User className="w-5 h-5" />
            Your AI Profile
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-blue-800">Skill Level:</span>
              <span className="ml-2 text-blue-700 capitalize">{userProfile.skillLevel}</span>
            </div>
            <div>
              <span className="font-medium text-blue-800">Preferred Complexity:</span>
              <span className="ml-2 text-blue-700 capitalize">{userProfile.preferredComplexity}</span>
            </div>
            <div>
              <span className="font-medium text-blue-800">Favorite Categories:</span>
              <span className="ml-2 text-blue-700">{userProfile.favoriteCategories.join(', ')}</span>
            </div>
          </div>
        </div>
      )}

      {!selectedNeed ? (
        <div className="space-y-6">
          {/* Common Needs */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">What do you need help with?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {COMMON_NEEDS.map((need) => (
                <button
                  key={need.id}
                  onClick={() => handleNeedSelect(need)}
                  className="group text-left p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:shadow-lg transition-all duration-200"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 ${need.color} rounded-lg text-white group-hover:scale-110 transition-transform`}>
                      <need.icon className="w-5 h-5" />
                    </div>
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium capitalize ${
                      need.complexity === 'beginner' ? 'bg-green-100 text-green-700' :
                      need.complexity === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {need.complexity}
                    </span>
                  </div>
                  <p className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                    {need.text}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">{need.category}</p>
                  <div className="flex items-center gap-1 mt-3 text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-sm font-medium">Get recommendations</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Need Input */}
          <div className="bg-gray-50 rounded-xl p-6">
            <h4 className="font-semibold text-gray-900 mb-3">Or describe your specific need:</h4>
            {!showCustomInput ? (
              <button
                onClick={() => setShowCustomInput(true)}
                className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-500 hover:text-purple-600 transition-colors"
              >
                <Brain className="w-6 h-6 mx-auto mb-2" />
                <span className="font-medium">Describe your custom need...</span>
              </button>
            ) : (
              <div className="space-y-3">
                <textarea
                  value={customNeed}
                  onChange={(e) => setCustomNeed(e.target.value)}
                  placeholder="e.g., I need an AI agent that can help me analyze customer feedback and generate actionable insights for my e-commerce business..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <div className="flex gap-3">
                  <button
                    onClick={handleCustomNeedSubmit}
                    disabled={!customNeed.trim()}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Get AI Recommendations
                  </button>
                  <button
                    onClick={() => {
                      setShowCustomInput(false);
                      setCustomNeed('');
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Selected Need */}
          <div className="bg-white border border-purple-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 ${selectedNeed.color} rounded-lg text-white`}>
                  <selectedNeed.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedNeed.text}</h3>
                  <p className="text-sm text-gray-600">{selectedNeed.category} • {selectedNeed.complexity}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedNeed(null);
                  setRecommendations([]);
                }}
                className="px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              >
                Change Need
              </button>
            </div>
            
            {selectedNeed.keywords.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedNeed.keywords.map((keyword, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <Brain className="w-12 h-12 text-purple-600 animate-pulse mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyzing Your Need</h3>
              <p className="text-gray-600">Finding the perfect AI agents for you...</p>
            </div>
          )}

          {/* Recommendations */}
          {recommendations.length > 0 && !loading && (
            <div>
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="w-6 h-6 text-purple-600" />
                <h3 className="text-xl font-semibold text-gray-900">
                  Recommended Agents ({recommendations.length})
                </h3>
              </div>

              <div className="space-y-6">
                {recommendations.map((rec, index) => (
                  <div
                    key={rec.agent.id}
                    className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      {/* Match Score */}
                      <div className="text-center">
                        <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-lg mb-2">
                          {rec.score}%
                        </div>
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${getMatchTypeColor(rec.matchType)}`}>
                          {getMatchTypeIcon(rec.matchType)} {rec.matchType}
                        </span>
                      </div>

                      {/* Agent Info */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-1">{rec.agent.name}</h4>
                            <p className="text-gray-600 text-sm mb-2">{rec.agent.description}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                <Star className="w-4 h-4" />
                                {rec.agent.stars}
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {rec.agent.last_updated}
                              </div>
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                {rec.agent.category}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => onAgentSelect?.(rec.agent)}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                          >
                            View Agent
                          </button>
                        </div>

                        {/* Match Reasons */}
                        <div>
                          <h5 className="font-medium text-gray-900 mb-2">Why this matches your need:</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {rec.reasons.map((reason, reasonIndex) => (
                              <div key={reasonIndex} className="flex items-center gap-2 text-sm text-gray-600">
                                <div className="w-1.5 h-1.5 bg-purple-500 rounded-full" />
                                {reason}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* More Recommendations */}
              <div className="text-center pt-6">
                <button className="px-6 py-3 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors">
                  Show More Recommendations
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});